import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
const { FiShield, FiCpu, FiActivity, FiAlertTriangle, FiCheck, FiX } = FiIcons;
import SafeIcon from '../common/SafeIcon';
import api from '../lib/api';
import toast from 'react-hot-toast';

export default function ConsentManager() {
  const [consents, setConsents] = useState([
    { id: 'ai_insights', label: 'AI Health Insights', icon: FiCpu, description: 'Allow neural modeling and crisis predictions.', status: 'granted' },
    { id: 'telemetry', label: 'Live Telemetry', icon: FiActivity, description: 'Stream vital signs to clinicians in real-time.', status: 'granted' },
    { id: 'emergency_dispatch', label: 'Auto-Dispatch (4422)', icon: FiAlertTriangle, description: 'Authorize emergency response on critical breach.', status: 'granted' }
  ]);
  const [loading, setLoading] = useState(false);

  const toggleConsent = async (id) => {
    const current = consents.find(c => c.id === id);
    const newStatus = current.status === 'granted' ? 'revoked' : 'granted';
    
    setLoading(true);
    try {
      await api.post('/users/me/consent', { purpose: id, status: newStatus });
      setConsents(consents.map(c => c.id === id ? { ...c, status: newStatus } : c));
      toast.success(`${current.label} ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update consent');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-indigo-500/10 rounded-xl flex items-center justify-center">
          <SafeIcon icon={FiShield} className="text-indigo-500" />
        </div>
        <div>
          <h3 className="font-black text-white">Consent & Privacy Controls</h3>
          <p className="text-xs text-slate-500">GDPR / EU AI Act Compliance v2.23.0</p>
        </div>
      </div>

      <div className="grid gap-4">
        {consents.map(item => (
          <div key={item.id} className="bg-slate-900 border border-slate-800 p-5 rounded-3xl flex items-center justify-between group hover:border-slate-700 transition-all">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-colors ${item.status === 'granted' ? 'bg-primary-500/10 text-primary-500' : 'bg-slate-800 text-slate-500'}`}>
                <SafeIcon icon={item.icon} className="text-xl" />
              </div>
              <div>
                <h4 className="font-bold text-white">{item.label}</h4>
                <p className="text-xs text-slate-500 max-w-[200px]">{item.description}</p>
              </div>
            </div>
            
            <button
              onClick={() => toggleConsent(item.id)}
              disabled={loading}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                item.status === 'granted' 
                  ? 'bg-emerald-500/10 text-emerald-500 hover:bg-red-500/10 hover:text-red-500' 
                  : 'bg-slate-800 text-slate-400 hover:bg-emerald-500/10 hover:text-emerald-500'
              }`}
            >
              {item.status === 'granted' ? 'Revoke' : 'Grant'}
            </button>
          </div>
        ))}
      </div>
      
      <div className="mt-8 p-4 bg-slate-800/50 rounded-2xl border border-dashed border-slate-700">
        <p className="text-[10px] text-slate-500 font-medium leading-relaxed">
          * Revoking consent for Auto-Dispatch will disable automated Protocol 4422 triggers. 
          Clinicians will still receive alerts, but manual authorization will be required for first-responder routing.
        </p>
      </div>
    </div>
  );
}